import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Paper, Typography, TextField, Button, Box, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, FormHelperText, Alert, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { RegisterData } from '../types/auth';
import CaptchaField from '../components/CaptchaField';
import PasswordField from '../components/PasswordField';
import { isCaptchaSiteKeyConfigured } from '../utils/captcha';

const Register: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { register: registerUser, error } = useAuth();
  const { register, handleSubmit, watch, formState: { errors } } = useForm<RegisterData & { confirmPassword: string }>();
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const captchaEnabled = useMemo(() => isCaptchaSiteKeyConfigured(), []);

  const password = watch('password');

  const onSubmit = async (data: RegisterData & { confirmPassword: string }) => {
    try {
      if (captchaEnabled && !captchaToken) {
        setCaptchaError(t('auth.captcha_required'));
        return;
      }
      setCaptchaError('');
      const { confirmPassword, ...registerData } = data;
      await registerUser({ ...registerData, captcha_token: captchaToken });
      navigate('/verify-email', { state: { email: data.email } });
    } catch (err) {
      setCaptchaToken('');
      setCaptchaResetKey((current) => current + 1);
      if (captchaEnabled) {
        setCaptchaError(t('auth.captcha_retry'));
      }
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: { xs: 3, sm: 6 }, px: { xs: 2, sm: 3 }, mb: 4 }}>
      <Paper 
        elevation={isMobile ? 0 : 3}
        sx={{ 
          p: { xs: 3, sm: 4 },
          borderRadius: { xs: 2, sm: 3 }
        }}
      >
        <Typography 
          variant={isMobile ? 'h5' : 'h4'} 
          component="h1" 
          sx={{ 
            textAlign: 'center', 
            mb: { xs: 2, sm: 3 },
            fontWeight: 600 
          }}
        >
          {t('common.register')}
        </Typography>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.75, sm: 2 } }}>
            <TextField
              fullWidth
              label={t('common.email')}
              type="email"
              autoComplete="email"
              error={!!errors.email}
              helperText={errors.email?.message}
              {...register('email', {
                required: t('auth.email_required'),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('auth.invalid_email'),
                },
              })}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '16px', sm: '14px' },
                }
              }}
            />

            <TextField
              fullWidth
              label={t('common.full_name')}
              autoComplete="name"
              error={!!errors.full_name}
              helperText={errors.full_name?.message}
              {...register('full_name', {
                required: t('auth.name_required'),
                minLength: {
                  value: 2,
                  message: t('auth.min_length', { length: 2 }),
                },
              })}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '16px', sm: '14px' },
                }
              }}
            />

            <FormControl fullWidth error={!!errors.user_type}>
              <InputLabel>{t('common.user_type')}</InputLabel>
              <Select
                label={t('common.user_type')}
                defaultValue=""
                {...register('user_type', {
                  required: t('auth.user_type_required'),
                })}
                sx={{
                  '& .MuiSelect-select': {
                    fontSize: { xs: '16px', sm: '14px' },
                  }
                }}
              >
                <MenuItem value="client">{t('common.client')}</MenuItem>
                <MenuItem value="professional">{t('common.professional')}</MenuItem>
              </Select>
              {errors.user_type && (
                <Typography variant="caption" color="error">
                  {errors.user_type.message}
                </Typography>
              )}
            </FormControl>

            <PasswordField
              fullWidth
              label={t('common.password')}
              autoComplete="new-password"
              error={!!errors.password}
              helperText={errors.password?.message}
              {...register('password', {
                required: t('auth.password_required'),
                minLength: {
                  value: 6,
                  message: t('auth.password_length'),
                },
              })}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '16px', sm: '14px' },
                }
              }}
            />

            <PasswordField
              fullWidth
              label={t('common.confirm_password')}
              autoComplete="new-password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              {...register('confirmPassword', {
                required: t('auth.password_required'),
                validate: value =>
                  value === password || t('auth.passwords_not_match'),
              })}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '16px', sm: '14px' },
                }
              }}
            />

            <FormControl error={!!errors.accepted_terms} sx={{ mt: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    {...register('accepted_terms', {
                      required: t('auth.accept_terms_required'),
                    })}
                  />
                }
                label={
                  <Typography variant="body2">
                    {t('auth.accept_terms_prefix')}{' '}
                    <Link to="/terms" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 500 }}>
                      {t('auth.accept_terms_link')}
                    </Link>{' '}
                    {t('auth.accept_terms_and')}{' '}
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 500 }}>
                      {t('auth.accept_terms_privacy_link')}
                    </Link>
                  </Typography>
                }
              />
              {errors.accepted_terms && (
                <FormHelperText>{errors.accepted_terms.message}</FormHelperText>
              )}
            </FormControl>

            {error && (
              <Alert severity="error">{error}</Alert>
            )}

            <CaptchaField
              resetKey={captchaResetKey}
              onTokenChange={(token) => {
                setCaptchaToken(token);
                if (token) {
                  setCaptchaError('');
                }
              }}
              errorText={captchaError}
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              sx={{ 
                mt: 1,
                py: { xs: 1.5, sm: 1.75 },
                fontSize: { xs: '1rem', sm: '1.1rem' }
              }}
            >
              {t('auth.sign_up')}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Typography variant={isMobile ? 'body2' : 'body1'}>
                {t('auth.have_account')}{' '}
                <Link 
                  to="/login" 
                  style={{ textDecoration: 'none', fontWeight: 500 }}
                >
                  {t('auth.sign_in')}
                </Link>
              </Typography>
            </Box>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default Register; 